/**
 * Custom Hook: useProvinces
 * Fetches provinces from API and provides loading/error states
 */

import { useState, useEffect } from 'react';
import { apiClient } from '../services/api';

export interface Province {
  id: string;
  nameTh: string;
  nameEn?: string;
  region?: string;
  displayOrder: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface UseProvincesResult {
  provinces: Province[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch provinces from API
 * @param activeOnly - If true, only fetch active provinces (default: true)
 * @returns Object containing provinces array, loading state, error, and refetch function
 */
export const useProvinces = (activeOnly: boolean = true): UseProvincesResult => {
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProvinces = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use all-details endpoint to get full province data
      const endpoint = activeOnly ? '/api/provinces/' : '/api/provinces/all-details';
      const response = await apiClient.get(endpoint);

      // Handle both possible response formats
      const data = response.data;
      // API returns: { success: true, data: { provinces: [...] } }
      const items = data.data?.provinces || data.items || data.provinces || data || [];

      // Convert string array to Province objects if needed
      const provinces: Province[] = Array.isArray(items)
        ? items.map((item: any, index: number) => {
            // If item is already a Province object
            if (typeof item === 'object' && item.nameTh) {
              return item;
            }
            // If item is a string, convert to Province object
            if (typeof item === 'string') {
              return {
                id: item,
                nameTh: item,
                nameEn: item,
                displayOrder: index,
                isActive: true
              };
            }
            return item;
          })
        : [];

      // Sort by displayOrder
      const sortedItems = provinces.sort((a: Province, b: Province) =>
        (a.displayOrder || 0) - (b.displayOrder || 0)
      );

      setProvinces(sortedItems);
    } catch (err: any) {
      console.error('Error fetching provinces:', err);
      setError(err.response?.data?.message || 'ไม่สามารถโหลดข้อมูลจังหวัดได้');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProvinces();
  }, [activeOnly]);

  return {
    provinces,
    loading,
    error,
    refetch: fetchProvinces
  };
};

export default useProvinces;
